import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import { RequestType, RequestTypeSlugEnum } from '@/types/requests';
import { Button, Tooltip, usePopoverContext } from '@electricjs/arc';
import {
  RequestStatusEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';
import { useNavigate } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { RequestCompleted } from './RequestCompleted';
import i18n from '@/i18n';
import {
  EXISTING_EMPLOYEE_ID_QUERY_PARAM,
  HRIS_OFFBOARDING_REQUEST_ID_QUERY_PARAM,
  HRIS_ONBOARDING_REQUEST_ID_QUERY_PARAM,
} from '@/constants/onboarding';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { useGetOrganizationQuery } from '@/redux/slices/organizationApiSlice';
import {
  useGetLatestOnboardingByEmployeeIdQuery,
  useGetRequestsForAnOnboardingQuery,
} from '@/redux/slices/onboardingApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  getOnboardingTooltipContent,
  getScheduledOnboardingInfo,
} from 'common/utils/onboardingHelpers';

type PrimaryButtonProps = {
  onClick?: () => void;
  children: string;
  loading?: boolean;
  isUpdating?: boolean;
  disabled?: boolean;
  tooltipContent?: string;
  showTooltip?: boolean;
};

const PrimaryButton = ({
  onClick,
  children,
  loading,
  isUpdating,
  disabled,
  tooltipContent,
  showTooltip,
}: PrimaryButtonProps) => {
  const button = (
    <Button
      id="button-mark-as-done"
      minWidth="12rem"
      alignSelf="center"
      showSkeleton={loading}
      onClick={onClick}
      loading={isUpdating}
      disabled={disabled}>
      {children}
    </Button>
  );

  if (showTooltip && tooltipContent) {
    return (
      <Tooltip
        content={tooltipContent}
        disabled={!showTooltip}
        placement="bottom-end"
        maxWidth="42rem">
        {button}
      </Tooltip>
    );
  }

  return button;
};

type PrimaryActionProps = {
  status?: RequestStatusEnum;
  requestType?: RequestType;
  requestId?: string;
  loading?: boolean;
  isDropDownMenu?: boolean;
  onBehalfOf?: string;
  onBehalfOfEmail?: string;
};

export const PrimaryAction = ({
  status,
  requestType,
  requestId = '',
  loading,
  isDropDownMenu,
  onBehalfOf,
  onBehalfOfEmail,
}: PrimaryActionProps) => {
  const navigate = useNavigate();
  const { toggle } = usePopoverContext();
  const { updateRequest, updateRequestLoading } = useUpdateRequestStatus({
    requestId,
  });

  // Additional hooks for HRIS onboarding validation
  const organizationId = useGetOrganizationId();
  const canOnboard = useUserHasOrganizationPermission(
    PermissionEnum.Onboardingswrite
  );

  const { data: organization } = useGetOrganizationQuery(
    organizationId && onBehalfOfEmail ? organizationId : skipToken
  );

  const { data: latestOnboarding, isFetching: isFetchingLatestOnboarding } =
    useGetLatestOnboardingByEmployeeIdQuery(
      organizationId &&
        onBehalfOf &&
        requestType?.slug === RequestTypeSlugEnum.HRIS_ONBOARDING
        ? { organizationId, employeeId: onBehalfOf }
        : skipToken
    );

  const shouldFetchOnboardingRequests = Boolean(
    organizationId && latestOnboarding?.id
  );

  const { data: onboardingRequests } = useGetRequestsForAnOnboardingQuery(
    shouldFetchOnboardingRequests && latestOnboarding
      ? { organizationId, onboardingId: latestOnboarding.id }
      : skipToken
  );
  const { hasScheduledOnboardingInFlight } = getScheduledOnboardingInfo(
    shouldFetchOnboardingRequests ? onboardingRequests : undefined
  );

  if (!status || !requestType) {
    return null;
  }

  if (status !== RequestStatusEnum.InProgress && !loading) {
    return <RequestCompleted />;
  }

  const handleEquipNewHire = () => {
    if (isDropDownMenu) {
      toggle();
    }

    setTimeout(() => {
      navigate(`/get-hardware?${SelectionParamKeys.EmployeeId}=${onBehalfOf}`);
    }, 300);
  };

  const handleOnboardNewHire = () => {
    if (isDropDownMenu) {
      toggle();
    }

    setTimeout(() => {
      navigate(
        `/onboardings/new?${EXISTING_EMPLOYEE_ID_QUERY_PARAM}=${onBehalfOf}&${HRIS_ONBOARDING_REQUEST_ID_QUERY_PARAM}=${requestId}`
      );
    }, 300);
  };

  const handleOffboardEmployee = () => {
    if (isDropDownMenu) {
      toggle();
    }

    setTimeout(() => {
      navigate(
        `/offboardings/${onBehalfOf}?${HRIS_OFFBOARDING_REQUEST_ID_QUERY_PARAM}=${requestId}`
      );
    }, 300);
  };

  const handleMarkAsDone = async () => {
    await updateRequest({ newStatus: RequestStatusEnum.Completed });
  };

  // HRIS onboarding specific logic
  if (requestType?.slug === RequestTypeSlugEnum.HRIS_ONBOARDING) {
    /**
     * Disable the onboarding button if:
     * - The user doesn't have permission to onboard
     * - The employee has no email
     * - The employee was already onboarded
     * - The employee has a scheduled onboarding request in flight
     * - We're still loading onboarding data
     */
    const shouldDisableOnboarding =
      !canOnboard ||
      !onBehalfOfEmail ||
      !!latestOnboarding ||
      hasScheduledOnboardingInFlight ||
      isFetchingLatestOnboarding;

    /**
     * Show the tooltip if:
     * - The user doesn't have permission to onboard
     * - The employee has no email
     * - The employee has a scheduled onboarding request in flight
     *
     * Don't show the tooltip for already onboarded employees (silent disable).
     */
    const shouldShowTooltip =
      !canOnboard || !onBehalfOfEmail || hasScheduledOnboardingInFlight;

    const tooltipContent = getOnboardingTooltipContent({
      canOnboard,
      hasEmail: !!onBehalfOfEmail,
      hasHrisIntegration: !!organization?.hrisIntegration,
      hasScheduledOnboardingInFlight,
      t: i18n.t,
    });

    return (
      <PrimaryButton
        onClick={handleOnboardNewHire}
        loading={loading}
        disabled={shouldDisableOnboarding}
        tooltipContent={tooltipContent}
        showTooltip={shouldShowTooltip}>
        {i18n.t('common:notificationItems.actions.onboard')}
      </PrimaryButton>
    );
  }

  if (requestType?.slug === RequestTypeSlugEnum.HARDWARE_PURCHASE) {
    return (
      <PrimaryButton onClick={handleEquipNewHire} loading={loading}>
        {i18n.t('common:notificationItems.actions.equipNewHire')}
      </PrimaryButton>
    );
  } else if (requestType?.slug === RequestTypeSlugEnum.HRIS_OFFBOARDING) {
    return (
      <PrimaryButton onClick={handleOffboardEmployee} loading={loading}>
        {i18n.t('common:notificationItems.actions.offboard')}
      </PrimaryButton>
    );
  }

  return (
    <PrimaryButton
      onClick={handleMarkAsDone}
      loading={loading}
      isUpdating={updateRequestLoading}>
      {i18n.t('common:notificationItems.actions.markAsDone')}
    </PrimaryButton>
  );
};
